import React, { useEffect, useState, useRef } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import MainCard from '../../ui-component/cards/MainCard';
import SecondaryAction from '../../ui-component/cards/CardSecondaryAction';
import { gridSpacing } from '../../store/constant';
import { IconBookDownload } from '@tabler/icons';
import axiosInstance from '../../axiosInstance';
import { styled } from '@mui/material/styles';
import configData from '../../config';
import { useSelector } from "react-redux";
import FixedCost from '../../assets/images/icons/FixedCost.png';
import materialissue from '../../assets/images/icons/MaterialIssue.png';
import Other_VariableCost from '../../assets/images/icons/other-variable-cost.png';
import grnlogo from '../../assets/images/icons/GRN.png';
import productionlogo from '../../assets/images/icons/Production.png';
import salesLogo from '../../assets/images/icons/Sales.png'; // Add Sales logo


const CardWrapper = styled(MainCard)(({ theme }) => ({
    backgroundColor: '#F44336',
    overflow: 'hidden',
    position: 'relative',
    textAlign: 'center',
    height: 150,
    color: 'white',
    '&:after': {
        content: '""',
        position: 'absolute',
        width: 190,
        height: 190,
        background: '#f6b9b99a',
        borderRadius: '50%',
        top: -85,
        right: -95,
        [theme.breakpoints.down('sm')]: {
            top: -105,
            right: -140
        }
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        width: 190,
        height: 190,
        background: '#f6b9b99a',
        borderRadius: '50%',
        top: -125,
        right: -15,
        opacity: 0.5,
        [theme.breakpoints.down('sm')]: {
            top: -155,
            right: -70
        }
    }
}));

function UploadFile() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [section, setSection] = useState(null);
    const fileInputRef = useRef(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const org_id = useSelector((state) => state?.account?.org?.id || state?.account?.org_data?.data?.id);
    const user_email = useSelector((state) => state?.account?.user?.email);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['csv', 'xlsx', 'xls'];
        if (allowedExtensions.includes(fileExtension)) {
            setSelectedFile(file);
            setFileInputKey(prevKey => prevKey + 1);
        } else {
            alert('Invalid file format. Please select a CSV, XLSX, or XLS file.');
        }
    };

    const handleUpload = async () => {
        if (selectedFile && section) {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('section', section);
            formData.append('org_id', org_id);
            formData.append('user_email', user_email);

            try {
                const response = await axiosInstance.post(`${configData.API_SERVER}/activity/upload_csv/`, formData);
                alert(`${response.data.message}`);
                fileInputRef.current.value = '';
                setSelectedFile(null);
                setSection(null);
            } catch (error) {
                alert(`Error: ${error.response?.data?.message || error.message}`);
            }
        }
    };

    useEffect(() => {
        if (selectedFile) handleUpload();
    }, [selectedFile]);

    const getTemplate = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <>
            <MainCard title="Upload File" secondary={<SecondaryAction />}>
                <input
                    key={fileInputKey}
                    type="file"
                    id="fileInput"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Grid container spacing={gridSpacing}>
                    {/* Material Issue Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('Material Issue');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={materialissue} alt="Material Issue" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">Material Issue</Typography>
                            </Box>
                        </CardWrapper>
                        <div 
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/Material+Issue-Template.xlsx', 'Material_Issue_Template.xlsx')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download Material Issue Template
                            </span>
                        </div>
                    </Grid>

                    {/* Other Variable Cost Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('Other Variable Cost');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={Other_VariableCost} alt="Other Variable Cost" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">Other Variable Cost</Typography>
                            </Box>
                        </CardWrapper>
                        <div 
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/Other_Variable_Cost-Template.xlsx', 'Other_Variable_Cost_Template.xlsx')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download Other Variable Cost Template
                            </span>
                        </div>
                    </Grid>

                    {/* Production Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('Production');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={productionlogo} alt="Production" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">Production</Typography>
                            </Box>
                        </CardWrapper>
                        <div 
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/Production-Template.xlsx', 'Production_Template.xlsx')}
                            style={{ cursor: 'pointer' }}    
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download Production Template
                            </span>
                        </div>
                    </Grid>

                    {/* Fixed Cost Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('Fixed Cost');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={FixedCost} alt="Fixed Cost" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">Fixed Cost</Typography>
                            </Box>
                        </CardWrapper>
                        <div
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/Fixed+Cost-Template.xlsx', 'Fixed_Cost_Template.xlsx')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download Fixed Cost Template
                            </span>
                        </div>
                    </Grid>

                    {/* GRN Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('GRN');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={grnlogo} alt="GRN" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">GRN</Typography>
                            </Box>
                        </CardWrapper>
                        <div 
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/GRN-Template.xlsx', 'GRN_Template.xlsx')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download GRN Template
                            </span>
                        </div>
                    </Grid>

                    {/* Sales Card */}
                    <Grid item lg={4} sm={6}>
                        <CardWrapper
                            onClick={() => {
                                setSection('Sales');
                                fileInputRef.current.click();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ p: 1, mt: 1 }}>
                                <img src={salesLogo} alt="Sales" style={{ width: 50, height: 50 }} />
                                <Typography variant="h4" color="inherit">Sales</Typography>
                            </Box>
                        </CardWrapper>
                        <div 
                            onClick={() => getTemplate('https://imagei.s3.ap-south-1.amazonaws.com/Template/Sales-Template.xlsx', 'Sales_Template.xlsx')}
                            style={{ cursor: 'pointer' }}    
                        >
                            <span>
                                <IconBookDownload stroke={1.5} size="1.2rem" />
                                Download Sales Template
                            </span>
                        </div>
                    </Grid>
                </Grid>
            </MainCard>
        </>
    );
}

export default UploadFile;
